// Copyright (c) 2026 Watch Tower Bible and Tract Society of Pennsylvania
// All Rights Reserved. Proprietary and confidential.
// Unauthorized copying, modification, or distribution is strictly prohibited.
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Folder, ChevronRight, ChevronLeft, FileDown, RefreshCw, CheckCircle2, XCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTH_LABELS = {
  jan: 'Janeiro', fev: 'Fevereiro', mar: 'Março', abr: 'Abril',
  mai: 'Maio', jun: 'Junho', jul: 'Julho', ago: 'Agosto',
  set: 'Setembro', out: 'Outubro', nov: 'Novembro', dez: 'Dezembro',
};

const formatBRL = (n) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(n || 0);

export default function S44ImportModal({ open, onOpenChange, onImport }) {
  // Navigation state
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [breadcrumb, setBreadcrumb] = useState([]); // [{id, name}]
  const [folders, setFolders] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  // Scan state
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);

  const loadFolders = useCallback(async (folderId) => {
    setLoadingList(true);
    try {
      const params = { category: 'manutencao' };
      if (folderId) params.parent_id = folderId;
      const res = await axios.get(`${API}/folders`, { params });
      setFolders(res.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao listar pastas');
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setCurrentFolderId(null);
      setBreadcrumb([]);
      setResult(null);
      loadFolders(null);
    }
  }, [open, loadFolders]);

  const enterFolder = (folder) => {
    setBreadcrumb((prev) => [...prev, { id: folder.id, name: folder.name }]);
    setCurrentFolderId(folder.id);
    setResult(null);
    loadFolders(folder.id);
  };

  const goBack = () => {
    const next = breadcrumb.slice(0, -1);
    const parentId = next.length > 0 ? next[next.length - 1].id : null;
    setBreadcrumb(next);
    setCurrentFolderId(parentId);
    setResult(null);
    loadFolders(parentId);
  };

  const scanCurrent = async () => {
    setScanning(true);
    setResult(null);
    try {
      const params = { category: 'manutencao' };
      if (currentFolderId) params.folder_id = currentFolderId;
      const res = await axios.get(`${API}/s44-import/scan`, { params });
      setResult(res.data);
      if (res.data.matched === 0) {
        toast.warning('Nenhum S-44 reconhecido. Verifique se os PDFs desta pasta são o formulário oficial S-44-T.');
      } else {
        toast.success(`${res.data.matched} mês(es) reconhecido(s) de ${res.data.scanned} PDF(s).`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao ler os relatórios');
    } finally {
      setScanning(false);
    }
  };

  const handleApply = () => {
    if (!result || !result.values) return;
    onImport(result.values);
    toast.success(`Preencheu ${Object.keys(result.values).length} mês(es) na previsão.`);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="s44-import-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileDown className="w-5 h-5" />
            Importar de Relatórios (S-44)
          </DialogTitle>
          <DialogDescription>
            Escolha a pasta em <strong>Manutenção</strong> onde estão os PDFs mensais S-44. O sistema lê o campo &quot;Total das despesas&quot; e preenche cada mês automaticamente.
          </DialogDescription>
        </DialogHeader>

        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground border-b pb-2">
          <Folder className="w-4 h-4" />
          <span>Manutenção</span>
          {breadcrumb.map((b) => (
            <span key={b.id} className="flex items-center gap-2">
              <ChevronRight className="w-3 h-3" />
              <span>{b.name}</span>
            </span>
          ))}
        </div>

        {/* Nav actions */}
        <div className="flex justify-between items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={goBack}
            disabled={breadcrumb.length === 0}
            data-testid="folder-back-button"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Voltar
          </Button>
          <Button
            onClick={scanCurrent}
            disabled={scanning}
            size="sm"
            data-testid="scan-folder-button"
          >
            {scanning ? (
              <>
                <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                Lendo PDFs...
              </>
            ) : (
              <>
                <FileDown className="w-4 h-4 mr-1" />
                Ler esta pasta
              </>
            )}
          </Button>
        </div>

        {/* Folder list */}
        {loadingList ? (
          <div className="flex items-center justify-center py-6">
            <RefreshCw className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        ) : folders.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            Sem subpastas. Clique em &quot;Ler esta pasta&quot; para escanear os PDFs aqui.
          </p>
        ) : (
          <div className="space-y-1">
            {folders.map((f) => (
              <button
                key={f.id}
                onClick={() => enterFolder(f)}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg border border-border hover:bg-accent transition text-left"
                data-testid={`folder-item-${f.id}`}
              >
                <Folder className="w-4 h-4 text-amber-600 shrink-0" />
                <span className="flex-1 truncate">{f.name}</span>
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </button>
            ))}
          </div>
        )}

        {/* Scan result */}
        {result && (
          <div className="border-t pt-3 space-y-3" data-testid="scan-result">
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-1">
                <span className="text-muted-foreground">PDFs escaneados:</span>
                <strong>{result.scanned}</strong>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-muted-foreground">Meses reconhecidos:</span>
                <strong className="text-emerald-700">{result.matched}</strong>
              </div>
            </div>

            {result.matched > 0 && (
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(result.values).map(([month, value]) => (
                  <div
                    key={month}
                    className="flex items-center justify-between px-3 py-2 rounded-lg bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200"
                    data-testid={`scan-value-${month}`}
                  >
                    <span className="text-sm font-medium">{MONTH_LABELS[month]}</span>
                    <span className="text-sm font-mono">{formatBRL(value)}</span>
                  </div>
                ))}
              </div>
            )}

            {result.details && result.details.length > 0 && (
              <details className="text-xs">
                <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                  Ver detalhes por arquivo ({result.details.length})
                </summary>
                <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                  {result.details.map((d, i) => (
                    <div
                      key={`${d.filename}-${i}`}
                      className="flex items-center gap-2 px-2 py-1 rounded border border-border"
                    >
                      {d.status === 'ok' ? (
                        <CheckCircle2 className="w-3 h-3 text-emerald-600 shrink-0" />
                      ) : (
                        <XCircle className="w-3 h-3 text-red-600 shrink-0" />
                      )}
                      <span className="flex-1 truncate">{d.filename}</span>
                      {d.status === 'ok' ? (
                        <span className="text-muted-foreground">
                          {MONTH_LABELS[d.month] || d.month} · {formatBRL(d.total_despesas)}
                        </span>
                      ) : (
                        <span className="text-red-600">{d.status}</span>
                      )}
                    </div>
                  ))}
                </div>
              </details>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                data-testid="cancel-import-button"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleApply}
                disabled={result.matched === 0}
                data-testid="apply-import-button"
              >
                Preencher previsão ({result.matched})
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
